import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import type { AuditEventEnvelope } from '@/types';
import { actorDisplayName, categoryMeta, describeEvent, isAlertRow } from './auditMeta';
import ActorChip from './ActorChip';

interface AuditEventRowProps {
  event: AuditEventEnvelope;
  onSelect: (event: AuditEventEnvelope) => void;
  selected?: boolean;
  onCopyIp?: (ip: string) => void;
  /** Deposit rows: clicking the entity label shows that deposit's full history. */
  onDrillDeposit?: (event: AuditEventEnvelope) => void;
  /** Rendered inside an expanded burst (indented). */
  nested?: boolean;
}

/** One audit event rendered as a human sentence with actor, time, and IP. */
const AuditEventRow: React.FC<AuditEventRowProps> = ({ event, onSelect, selected, onCopyIp, onDrillDeposit, nested }) => {
  const cat = categoryMeta(event.category);
  const warn = isAlertRow(event);
  const sentence = describeEvent(event);
  const ts = new Date(event.ts);
  const canDrill = !!onDrillDeposit && event.entityType === 'deposit' && !!event.entityId;

  return (
    <tr
      className={`cursor-pointer hover:bg-blue-50/40 ${selected ? 'bg-blue-50' : warn ? 'bg-red-50/40' : nested ? 'bg-gray-50/60' : ''}`}
      onClick={() => onSelect(event)}
    >
      {/* Time — relative with absolute beneath, full ISO on hover */}
      <td
        className={`whitespace-nowrap px-3 py-2 align-top text-gray-500 ${nested ? 'border-l-2 border-blue-200 pl-6' : ''}`}
        title={ts.toISOString()}
      >
        <div>{formatDistanceToNow(ts, { addSuffix: true })}</div>
        <div className="text-[10px] text-gray-400">{format(ts, 'MMM d, h:mm a')}</div>
      </td>

      {/* Event — icon chip + human sentence */}
      <td className="px-3 py-2 align-top">
        <div className="flex items-start gap-2">
          <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${cat.bg}`}>
            <i className={`fas ${cat.icon} ${cat.color} text-[10px]`} aria-hidden="true" />
          </span>
          <span className={`leading-snug ${warn ? 'text-red-700' : 'text-gray-700'}`}>
            <span className="font-semibold text-gray-900">{actorDisplayName(event)}</span>
            <span> {sentence.verb}</span>
            {sentence.change && (
              <span className="font-medium text-gray-900"> {sentence.change}</span>
            )}
            {sentence.onLabel && (
              <>
                <span className="text-gray-400"> on </span>
                {canDrill ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrillDeposit?.(event);
                    }}
                    className="font-medium text-blue-700 hover:underline focus:underline focus:outline-none"
                    title="Show this deposit's full history"
                    aria-label={`Show full history for deposit ${sentence.onLabel}`}
                  >
                    {sentence.onLabel}
                  </button>
                ) : (
                  <span className="font-medium text-gray-700">{sentence.onLabel}</span>
                )}
              </>
            )}
            {warn && <i className="fas fa-triangle-exclamation ml-1 text-[10px] text-red-400" aria-label="Security event" />}
          </span>
        </div>
      </td>

      {/* Actor cell (avatar + company tag) */}
      <td className="px-3 py-2 align-top">
        <ActorChip event={event} />
      </td>

      {/* IP — click to copy */}
      <td
        className="whitespace-nowrap px-3 py-2 align-top font-mono text-[10px] text-gray-400"
        onClick={(e) => {
          if (event.ipAddress && onCopyIp) {
            e.stopPropagation();
            onCopyIp(event.ipAddress);
          }
        }}
        title={event.ipAddress ? 'Click to copy' : undefined}
      >
        {event.ipAddress || '—'}
      </td>
    </tr>
  );
};

export default AuditEventRow;
